import { useEffect } from "react";

import { useAtom } from "jotai";

import { detectExtensionGate } from "@prostocheck/kep";
import { isExtensionGateConnectedAtom } from "@prostocheck/shared";

/**
 * Re-probes the jkurwa-web gate extension once, on mount, but only when the
 * last check said "not connected" — `useExtensionGate` (`libs/data-web`) only
 * ever checks once, at app start, so a user who installs or enables the
 * extension after landing on `/login` and comes back to it (no full reload in
 * between) would otherwise keep seeing the stale "not connected" banner for
 * the rest of the tab's life. Meant to be called from `ExtensionGateAlert`,
 * so every visit to the login page gets a fresh answer. See
 * ai/docs/kep-signing.md.
 *
 * Reads `isConnected` only at mount time — deliberately left off the
 * dependency array so this does not re-fire on the very `setIsConnected` call
 * it makes itself.
 */
export const useExtensionGateRecheck = (): void => {
  const [isConnected, setIsConnected] = useAtom(isExtensionGateConnectedAtom);

  useEffect(() => {
    if (isConnected !== false) return;

    let isCurrent = true;

    void detectExtensionGate(undefined, { force: true }).then(isAvailable => {
      if (isCurrent) setIsConnected(isAvailable);
    });

    return () => {
      isCurrent = false;
    };
  }, [setIsConnected]);
};
